/**
 * RequiredLorFieldsPanel
 *
 * Renders LoR template fields using the same EditableFieldRow visual language
 * used across the Review and Finalize panels for consistency. Values are
 * managed as local drafts and committed to the parent on save.
 */
import React, { useEffect, useMemo, useState } from 'react';

import { type FieldReviewMap } from '@/components/agent-chat/messages/lorUtils';
import {
  EditableFieldRow,
  type FieldReviewInfo,
} from '@/components/claims-filing/common/EditableFieldRow';
import { FieldSection } from '@/components/claims-filing/common/FieldSection';
import { EnhancedSelect } from '@/components/letters/create-lor/shared/EnhancedSelect';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DOCUMENT_FIELD_MAPPING_DISPLAY_NAMES,
  DocumentFieldMapping,
  type LorDisplayField,
  LoRScalarChoice,
} from '@/services/types/letters-types';

type FieldValue = string | LoRScalarChoice;

// Regex to detect Jinja2 template placeholders like {{claim_number}}
const JINJA_PLACEHOLDER_REGEX = /^\{\{[^}]+\}\}$/;

const isJinjaPlaceholder = (value: string): boolean => {
  return JINJA_PLACEHOLDER_REGEX.test(value.trim());
};

const toDisplayValue = (value: FieldValue | undefined): string => {
  if (typeof value === 'string') {
    // Don't display raw Jinja2 template placeholders
    if (isJinjaPlaceholder(value)) return '';
    return value;
  }
  if (value && typeof value === 'object') {
    const v = value.value ?? '';
    // Don't display raw Jinja2 template placeholders
    if (isJinjaPlaceholder(v)) return '';
    return v;
  }
  return '';
};

const isChoiceArray = (
  field: LorDisplayField,
): LoRScalarChoice[] | null => {
  if (!Array.isArray(field.default_value)) return null;
  const filtered = field.default_value.filter(
    (choice): choice is LoRScalarChoice =>
      Boolean(
        choice &&
          typeof choice === 'object' &&
          Object.prototype.hasOwnProperty.call(choice, 'value'),
      ),
  );
  return filtered.length > 0 ? filtered : null;
};

const renderContactTrigger = (value: LoRScalarChoice | string) => {
  if (typeof value === 'string') {
    return <span className="font-medium text-gray-900">{value}</span>;
  }

  const handleType = value.context?.contact_handle?.handle_type;
  const contactSource = value.context?.contact_source;

  return (
    <span className="flex items-center gap-2">
      <span className="font-medium text-gray-900">{value.value}</span>
      {handleType && (
        <Badge
          variant="secondary"
          className="text-xs px-1.5 py-0.5 bg-blue-50 border-blue-200 text-blue-700"
        >
          {handleType.toLowerCase()}
        </Badge>
      )}
      {contactSource && (
        <Badge
          variant="outline"
          className="text-xs px-1.5 py-0.5 bg-gray-50 border-gray-300 text-gray-600"
        >
          {contactSource}
        </Badge>
      )}
    </span>
  );
};

const renderContactChoice = (choice: LoRScalarChoice) => {
  const handleType = choice.context?.contact_handle?.handle_type;
  const contactSource = choice.context?.contact_source;

  return (
    <div className="flex items-center justify-between w-full">
      <span className="font-medium text-gray-900">{choice.value}</span>
      <div className="flex items-center gap-1">
        {handleType && (
          <Badge
            variant="secondary"
            className="text-xs px-2 py-0.5 bg-blue-50 border-blue-200 text-blue-700"
          >
            {handleType.toLowerCase()}
          </Badge>
        )}
        {contactSource && (
          <Badge
            variant="outline"
            className="text-xs px-2 py-0.5 bg-gray-50 border-gray-300 text-gray-600"
          >
            {contactSource}
          </Badge>
        )}
      </div>
    </div>
  );
};

interface ChoiceFieldRowProps {
  id: string;
  label: string;
  value: FieldValue | undefined;
  required: boolean;
  choices: LoRScalarChoice[];
  hoverKey?: string;
  onHover?: (key: string | null) => void;
  onDraftChange: (next: FieldValue) => void;
  onSave: () => void;
  onCancel: () => void;
  renderTriggerContent?: (value: LoRScalarChoice | string) => React.ReactNode;
  renderChoiceContent?: (choice: LoRScalarChoice) => React.ReactNode;
  allowManualEntry?: boolean;
  /** Review status from FinchGPT - displays red highlight and tooltip when problematic */
  reviewInfo?: FieldReviewInfo;
}

const ChoiceFieldRow: React.FC<ChoiceFieldRowProps> = ({
  id,
  label,
  value,
  required,
  choices,
  hoverKey,
  onHover,
  onDraftChange,
  onSave,
  onCancel,
  renderTriggerContent,
  renderChoiceContent,
  allowManualEntry = false,
  reviewInfo,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const handleMouseEnter = () => onHover?.(hoverKey || id);

  const displayValue = toDisplayValue(value);
  const currentHandleType =
    typeof value === 'object' && value
      ? value.context?.contact_handle?.handle_type
      : undefined;

  // Check if field has problematic review status from FinchGPT
  const isProblematic = reviewInfo?.isProblematic ?? false;
  const hasReviewNotes = Boolean(reviewInfo?.notes);

  if (!isEditing) {
    return (
      <div
        role="button"
        aria-label={`${label} ${required ? '(required)' : '(optional)'}${isProblematic ? ' - needs attention' : ''}`}
        tabIndex={0}
        onClick={() => setIsEditing(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsEditing(true);
          }
        }}
        onMouseEnter={handleMouseEnter}
        onFocus={handleMouseEnter}
        className={`group px-3 py-2 transition-colors ${
          isProblematic
            ? 'border-l-4 border-l-red-500 bg-red-50/50 hover:bg-red-50/70'
            : 'hover:bg-indigo-50/30'
        }`}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={`text-sm font-semibold truncate ${isProblematic ? 'text-red-800' : 'text-gray-900'}`}
            >
              {label}
            </span>
            {/* Show review status indicator with tooltip */}
            {isProblematic && hasReviewNotes ? (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    aria-label="FinchGPT flagged issue"
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-700 rounded cursor-help"
                  >
                    ⚠️ {reviewInfo?.status}
                  </span>
                </TooltipTrigger>
                <TooltipContent
                  side="top"
                  className="max-w-xs bg-red-900 text-white border-red-700"
                >
                  <p className="text-xs font-medium">FinchGPT Review:</p>
                  <p className="text-xs mt-1">{reviewInfo?.notes}</p>
                </TooltipContent>
              </Tooltip>
            ) : isProblematic ? (
              <span
                aria-label="Needs attention"
                className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-red-100 text-red-700 rounded"
              >
                ⚠️ {reviewInfo?.status}
              </span>
            ) : null}
            {!required && (
              <span className="text-[10px] uppercase tracking-wide text-gray-500">
                Optional
              </span>
            )}
          </div>
          <div className="opacity-0 group-hover:opacity-100 transition-opacity">
            <Button
              size="sm"
              variant="outline"
              className="text-xs"
              onClick={() => setIsEditing(true)}
              aria-label={`Edit ${label}`}
            >
              Edit
            </Button>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2 text-sm">
          {displayValue ? (
            <span
              className={`font-medium truncate ${isProblematic ? 'text-red-700' : 'text-gray-900'}`}
            >
              {displayValue}
            </span>
          ) : (
            <span className={isProblematic ? 'text-red-400' : 'text-gray-400'}>
              —
            </span>
          )}
          {currentHandleType && (
            <Badge
              variant="secondary"
              className="text-xs px-1.5 py-0.5 bg-blue-50 border-blue-200 text-blue-700"
            >
              {currentHandleType.toLowerCase()}
            </Badge>
          )}
        </div>
      </div>
    );
  }

  return (
    <div
      role="group"
      aria-label={`${label} ${required ? '(required)' : '(optional)'}`}
      onMouseEnter={handleMouseEnter}
      onFocus={handleMouseEnter}
      className="px-3 py-2 transition-colors bg-indigo-50/40 ring-2 ring-indigo-200"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-gray-900 truncate">
            {label}
          </span>
          {!required && (
            <span className="text-[10px] uppercase tracking-wide text-gray-500">
              Optional
            </span>
          )}
        </div>
      </div>
      <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:gap-2">
        <div className="flex-1 min-w-0">
          <EnhancedSelect
            value={value}
            onChange={onDraftChange}
            choices={choices}
            allowManualEntry={allowManualEntry}
            renderTriggerContent={renderTriggerContent}
            renderChoiceContent={renderChoiceContent}
            placeholder={`Select ${label.toLowerCase()}...`}
          />
        </div>
        <div className="mt-2 sm:mt-0 sm:w-48 flex items-center justify-end gap-2">
          <Button
            size="sm"
            variant="ghost"
            className="text-xs"
            onClick={() => {
              onCancel();
              setIsEditing(false);
            }}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="text-xs"
            onClick={() => {
              onSave();
              setIsEditing(false);
            }}
          >
            Update
          </Button>
        </div>
      </div>
    </div>
  );
};

type Props = {
  fields: LorDisplayField[];
  values: Record<string, FieldValue>;
  isLoading?: boolean;
  onChange: (mapping: string, value: FieldValue) => void;
  /**
   * Optional: notify parent of in-progress edits (draft values) as the user types.
   * This does not imply persistence; parent can choose how to handle drafts.
   */
  onDraftChange?: (mapping: string, value: FieldValue) => void;
  /** Optional: report hover so parent can sync right-pane evidence */
  onHover?: (key: string | null) => void;
  /**
   * Optional: field review data from FinchGPT LOR review.
   * When provided, fields flagged as problematic (EDIT, MISSING) will be
   * highlighted in red with tooltips showing the AI's notes.
   */
  fieldReviewMap?: FieldReviewMap;
};
export const RequiredLorFieldsPanel: React.FC<Props> = ({
  fields,
  values,
  isLoading = false,
  onChange,
  onDraftChange,
  onHover,
  fieldReviewMap = {},
}) => {
  // Local draft keyed by field.id to avoid collisions when multiple fields share the same mapping
  const [draft, setDraft] = useState<Record<string, FieldValue>>({});
  const dirtyRef = React.useRef<Set<string>>(new Set());

  const getMergeKey = (f: LorDisplayField): string => {
    const ref = (f.reference || '').trim();
    if (ref) return ref;
    return f.id;
  };

  useEffect(() => {
    // Initialize draft by field id from incoming values keyed by merge key
    const next: Record<string, FieldValue> = {};
    fields.forEach((f) => {
      const key = getMergeKey(f);
      const v = (values as Record<string, FieldValue | undefined>)[key];
      if (v !== undefined) next[f.id] = v as FieldValue;
    });
    setDraft(next);
  }, [values, fields]);

  const visibleFields = useMemo(() => {
    // Show all fields; sort required first for clarity
    const all = [...fields];
    all.sort(
      (a, b) => Number(Boolean(b.required)) - Number(Boolean(a.required)),
    );
    return all;
  }, [fields]);

  // Helper to get review info for a field by its mapping key
  const getFieldReviewInfo = (mappingKey: string): FieldReviewInfo | undefined => {
    // Try the mapping key directly (e.g., CLAIM_NUMBER)
    if (fieldReviewMap[mappingKey]) {
      return fieldReviewMap[mappingKey];
    }
    // Try uppercase version
    const upperKey = mappingKey.toUpperCase();
    if (fieldReviewMap[upperKey]) {
      return fieldReviewMap[upperKey];
    }
    // Try with underscores replaced by spaces
    const spacedKey = mappingKey.replace(/_/g, ' ').toUpperCase();
    if (fieldReviewMap[spacedKey]) {
      return fieldReviewMap[spacedKey];
    }
    return undefined;
  };

  // Check if there are any problematic fields to show summary
  const problematicFieldCount = useMemo(() => {
    return Object.values(fieldReviewMap).filter((r) => r.isProblematic).length;
  }, [fieldReviewMap]);

  if (isLoading) {
    return (
      <div className="p-3 space-y-3">
        <div className="text-xs text-gray-500">Loading fields…</div>
        <div className="grid gap-3">
          <div className="h-9 bg-gray-100 rounded" />
          <div className="h-9 bg-gray-100 rounded" />
          <div className="h-9 bg-gray-100 rounded" />
        </div>
      </div>
    );
  }

  if (visibleFields.length === 0) {
    return <div className="p-3 text-xs text-gray-500">No fields</div>;
  }

  return (
    <FieldSection className="border-0">
      <div className="px-4 py-2 flex items-center justify-between gap-4">
        <div
          className="text-xs text-gray-500"
          role="note"
          aria-label="LoR fields disclaimer"
        >
          Editing values here is used solely for generating the Letter of{' '}
          Representation and may not persist to the underlying legal matter
          record.
        </div>
        {problematicFieldCount > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center gap-1.5 px-2 py-1 bg-red-50 border border-red-200 rounded-md cursor-help">
                <span className="text-red-600">⚠️</span>
                <span className="text-xs font-medium text-red-700">
                  {problematicFieldCount} field{problematicFieldCount > 1 ? 's' : ''} flagged
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="max-w-xs">
              <p className="text-xs">
                FinchGPT has flagged {problematicFieldCount} field{problematicFieldCount > 1 ? 's' : ''} that may need attention.
                Hover over highlighted fields to see details.
              </p>
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {visibleFields.map((field) => {
        const label =
          field.display_name ||
          DOCUMENT_FIELD_MAPPING_DISPLAY_NAMES[field.reference as DocumentFieldMapping] ||
          field.reference;

        const mergeKey = getMergeKey(field);
        const draftValue =
          draft[field.id] !== undefined ? draft[field.id] : values[mergeKey];

        const choiceOptions = isChoiceArray(field);
        const isContactField =
          field.reference === DocumentFieldMapping.ADJUSTER_CONTACT;

        if (choiceOptions && choiceOptions.length > 0) {
          // Show ALL contact choices (don't filter by adjuster) so user can pick
          // carrier email even if they selected an adjuster with only fax
          let selectableChoices = choiceOptions;

          // For LoR, only allow fax/email contacts (no phone)
          if (isContactField) {
            selectableChoices = selectableChoices.filter((c) => {
              const handleType = String(
                c.context?.contact_handle?.handle_type || '',
              ).toUpperCase();
              return handleType.includes('FAX') || handleType.includes('EMAIL');
            });
          }

          return (
            <ChoiceFieldRow
              key={`choice-${field.id}-${field.reference}`}
              id={`lor:${mergeKey}`}
              label={label}
              value={draftValue}
              required={Boolean(field.required)}
              choices={selectableChoices}
              hoverKey={`lor:${mergeKey}`}
              onHover={onHover}
              allowManualEntry={!isContactField}
              renderTriggerContent={
                isContactField ? renderContactTrigger : undefined
              }
              renderChoiceContent={
                isContactField ? renderContactChoice : undefined
              }
              onDraftChange={(next) => {
                // Ensure field isolation - only update the specific field
                const fieldMapping = mergeKey;
                dirtyRef.current.add(field.id);
                setDraft((prev) => ({ ...prev, [field.id]: next }));
                if (onDraftChange) onDraftChange(fieldMapping, next);
              }}
              onSave={() => {
                // Persist explicit empty string when user clears the field
                const nextValue =
                  draft[field.id] !== undefined
                    ? (draft[field.id] as FieldValue)
                    : ((values[mergeKey] ?? '') as FieldValue);
                onChange(mergeKey, nextValue as FieldValue);
              }}
              onCancel={() => {
                // Ensure field isolation - only reset the specific field
                setDraft((prev) => ({
                  ...prev,
                  [field.id]: values[mergeKey] ?? '',
                }));
              }}
              reviewInfo={getFieldReviewInfo(mergeKey)}
            />
          );
        }

        const displayValue = toDisplayValue(draftValue);

        return (
          <EditableFieldRow
            key={`editable-${field.id}-${field.reference}`}
            id={`lor:${mergeKey}`}
            label={label}
            value={displayValue}
            required={Boolean(field.required)}
            activateOnRowClick
            hoverKey={`lor:${mergeKey}`}
            onHover={onHover}
            onChange={(next: string) => {
              // Ensure field isolation - only update the specific field
              const fieldMapping = mergeKey;
              dirtyRef.current.add(field.id);
              setDraft((prev) => ({ ...prev, [field.id]: next }));
              if (onDraftChange) onDraftChange(fieldMapping, next);
            }}
            onSave={() =>
              onChange(mergeKey, (draft[field.id] ?? '') as FieldValue)
            }
            onCancel={() => {
              // Ensure field isolation - only reset the specific field
              setDraft((prev) => ({
                ...prev,
                [field.id]: values[mergeKey] ?? '',
              }));
            }}
            mode="editable"
            size="compact"
            reviewInfo={getFieldReviewInfo(mergeKey)}
          />
        );
      })}
    </FieldSection>
  );
};
